import { parseToken } from '../../../Shared/Factsheet/contradictions/detector'

/**
 * Resolves a human-readable label for a contradiction spec token from the
 * factsheet blueprint (`state.factsheet.blueprint`).
 *
 * - plain fact  → the fact's label
 * - fact + attr → "<fact label> + <attribute label>"
 *
 * Falls back to the raw token if the blueprint doesn't know the id (e.g. it
 * isn't loaded yet, or the id is missing from the tree).
 *
 * @param {Object} blueprint state.factsheet.blueprint
 * @param {string} token spec token, e.g. 'F:251' or 'f65:a608'
 * @returns {string}
 */
export const resolveFactLabel = (blueprint, token) => {
  const parsed = parseToken(token)
  const factTree = blueprint?.factTree
  const factLabel = factTree?.facts?.[parsed.factId]?.label

  if (parsed.attrId) {
    const attrLabel = factTree?.attributes?.[parsed.attrId]?.label
    if (factLabel && attrLabel) {
      return `${factLabel} + ${attrLabel}`
    }
    if (attrLabel) {
      return attrLabel
    }
  }

  return factLabel ?? token
}

/**
 * Resolves the plain fact label for a contradiction token, suitable for driving
 * the factsheet search (which matches fact labels only, never attribute labels).
 *
 * Unlike {@link resolveFactLabel}, this never appends the attribute part.
 *
 * @param {Object} blueprint state.factsheet.blueprint
 * @param {string} token spec token, e.g. 'F:251' or 'f65:a608'
 * @returns {string|null} the fact label, or null if it can't be resolved
 */
export const resolveFactSearchTerm = (blueprint, token) => {
  const parsed = parseToken(token)
  return blueprint?.factTree?.facts?.[parsed.factId]?.label ?? null
}

/**
 * Extracts the fact def-id from a contradiction token, used to jump straight to
 * that fact in the factsheet.
 *
 * @param {string} token spec token, e.g. 'F:251' or 'f65:a608'
 * @returns {string} the fact def-id
 */
export const resolveFactId = (token) => parseToken(token).factId
